<template>
  <div data-testid="setup-fee-markets-table">
    <data-table
      test-id-prefix="setup-fee-markets"
      :config="tableConfig"
      :data="displayRows"
      display-mode="table"
      :refreshButton="{ show: true }"
      @button-click="handleButtonClick"
      @edit-click="handleEditClick"
      @refresh="handleRefresh"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { useSetupFee } from '../../../composables/useSetupFee'
import { log } from '../../../utils/DebugLogger'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import type { SetupFee_DefaultRow } from '../../../contracts/SetupFee_DefaultRow'

const { t, n } = useI18n()
const { defaultsList, fetchDefaults, saveDefault } = useSetupFee()

const CURRENCY_LOCALE: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  CLP: 'es-CL',
}

function priceDisplay(row: SetupFee_DefaultRow): string {
  const locale = CURRENCY_LOCALE[row.currency] ?? 'pt-BR'
  const factor = currencyFactor(locale, row.currency)
  try {
    return n(row.amount_cents / factor, { style: 'currency', currency: row.currency, currencyDisplay: 'symbol' })
  } catch {
    return `${row.currency} ${(row.amount_cents / factor).toFixed(2)}`
  }
}

// One display row per market: formatted price, joined countries and the
// 1/0 shape the active_inactive renderType expects (contract active is bool).
const displayRows = computed(() =>
  defaultsList.value.map((row) => ({
    ...row,
    row_key: `${row.currency}-${row.provider_code}`,
    price_display: priceDisplay(row),
    countries_display: (row.countries ?? []).join(', ') || '—',
    active_display: row.active === false ? 0 : 1,
  })),
)

const tableConfig = computed(() => ({
  tableId: 'tabelaSetupFeeMarkets',
  title: t('Mercados da taxa de implantação'),
  columns: [
    {
      // The row's DB id as the clickable id — the way into the edit modal,
      // same idiom as the other staff tables.
      data: 'id',
      title: t('ID'),
      width: 80,
      sortable: true,
      renderType: 'custom_clickable_id',
    },
    {
      data: 'currency',
      title: t('Moeda'),
      width: 90,
      sortable: true,
    },
    {
      data: 'provider_code',
      title: t('Gateway'),
      width: 130,
      sortable: true,
    },
    {
      data: 'price_display',
      title: t('Valor sugerido'),
      width: 140,
      sortable: false,
    },
    {
      data: 'max_installments',
      title: t('Parcelas máx.'),
      width: 110,
      sortable: true,
      renderType: 'integer',
    },
    {
      data: 'countries_display',
      title: t('Países'),
      width: 200,
      sortable: false,
    },
    {
      data: 'active_display',
      title: t('Ativo'),
      width: 80,
      sortable: true,
      renderType: 'active_inactive',
    },
    {
      data: null,
      title: t('Ativar/Desativar'),
      width: 130,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Ativar/Desativar'),
      buttonIcon: 'pi pi-power-off',
      buttonAction: 'toggle-active',
      buttonTestId: (row: Record<string, unknown>) =>
        `setup-fee-market-toggle-row-${row.currency}-${row.provider_code}`,
    },
  ],
  footer: false,
  rowTestId: (row: Record<string, unknown>) =>
    `setup-fee-market-row-${row.currency}-${row.provider_code}`,
}))

async function loadData(): Promise<void> {
  try {
    await fetchDefaults()
  } catch (err) {
    log('SetupFeeMarketsTable', 'loadData', 'Erro ao carregar mercados', err)
  }
}

/**
 * Deactivate/reactivate in place — same upsert endpoint, only `active` flips.
 * An inactive row leaves the emit resolution (findActiveByCountry) but stays
 * listed here (list-all read) so staff can bring it back.
 */
async function handleToggleActive(row: SetupFee_DefaultRow): Promise<void> {
  try {
    await saveDefault({
      currency: row.currency,
      provider_code: row.provider_code,
      amount_cents: row.amount_cents,
      max_installments: row.max_installments,
      countries: Array.isArray(row.countries) ? [...(row.countries as string[])] : [],
      active: row.active === false,
    })
    eventBus.emit('globalMessage.success', row.active === false ? t('Mercado reativado') : t('Mercado desativado'))
  } catch (err) {
    log('SetupFeeMarketsTable', 'handleToggleActive', 'Erro ao alternar mercado', err)
    eventBus.emit('globalMessage.error', t('Erro ao alterar o mercado de taxa de implantação'))
  }
}

function handleButtonClick(e: { value: unknown; data: SetupFee_DefaultRow; action: string }): void {
  if (e.action === 'toggle-active') {
    void handleToggleActive(e.data)
  }
}

/** Clicking the market's clickable ID opens the edit modal. */
function handleEditClick(value: unknown): void {
  const row = defaultsList.value.find((r) => r.id === Number(value))
  if (row) {
    eventBus.emit('modal.setupFeeMarket.open', { market: row })
  }
}

function handleRefresh(): void {
  void loadData()
}

onMounted(() => {
  eventBus.on('setupFeeMarkets.refresh', handleRefresh)
})

onBeforeUnmount(() => {
  eventBus.off('setupFeeMarkets.refresh', handleRefresh)
})

defineExpose({ loadData })
</script>
